import { useCallback, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import { useLocalStorage } from "@/hooks";
import { captureSentryException } from "@/lib/sentry";
import { toast } from "@/ui";

import {
  getI18nLoading,
  loadLanguage,
  setI18nLoading,
  subscribeI18nLoading,
  syncDocumentLang,
} from "./i18n";
import { Language, TranslateFunction } from "./i18n.types";

export const useT = () => {
  const { t } = useTranslation();
  return t as TranslateFunction;
};

export const useLanguage = () => {
  const { i18n, t } = useTranslation();
  const [_, setLanguage] = useLocalStorage<string | null | undefined>("language", null);

  const changeLanguage = useCallback(
    async (language: Language) => {
      if (i18n.language === language) return;
      setI18nLoading(true);
      try {
        await loadLanguage(language);
        await i18n.changeLanguage(language);
        syncDocumentLang(language);
        setLanguage(language);
      } catch (err) {
        captureSentryException(err, {
          tags: { source: "i18n.changeLanguage" },
          extra: { locale: language },
        });
        toast.error(t("general.errorState.unknown"));
      } finally {
        setI18nLoading(false);
      }
    },
    [i18n, setLanguage, t]
  );

  return [i18n.language as Language, changeLanguage] as const;
};

export const useIsI18nLoading = () =>
  useSyncExternalStore(subscribeI18nLoading, getI18nLoading, getI18nLoading);
